import { useEffect, useRef, useState } from 'react';
import { useSignIn, useSignUp } from '@clerk/clerk-react';
import { Button, ErrorNotice, Field, TextInput } from './primitives';

/**
 * The shared BCE sign-in screen.
 *
 * One implementation, used by every BCE front door: the client portal
 * (mybcecloud), the internal admin (app.bcecloud.com), and — transcribed by
 * hand into server-rendered HTML — executive analytics. Split-screen: brand
 * column left, gradient/photo hero right, collapsing to a single column below
 * 720px.
 *
 * Everything brand-specific is a prop, so a tenant portal and the BCE-internal
 * tool render from the same component without either forking it.
 */

// ─── Layout ──────────────────────────────────────────────────────────────────

export interface SignInHero {
  /** Gradient start — typically the brand's primary color. */
  readonly fromColor: string;
  /** Gradient end — typically the darker sidebar color. */
  readonly toColor: string;
  /** Optional photo laid over the gradient, darkened so the copy stays legible. */
  readonly imageUrl?: string;
  /** The pull quote. Rendered with typographic quote marks. */
  readonly quote?: React.ReactNode;
  /** A single headline figure, e.g. "250+". */
  readonly statValue?: string;
  /** What the figure counts. */
  readonly statLabel?: string;
}

export interface SignInLayoutProps {
  /** The logo or wordmark, top-left. */
  readonly masthead: React.ReactNode;
  /** Small tracked label beside the mark, e.g. "TEAM EXPERIENCES". */
  readonly mastheadLabel?: string;
  /** The chip above the headline, e.g. "BEST CORPORATE EVENTS". */
  readonly eyebrow?: string;
  /** The headline. Newlines are honoured, so line breaks are deliberate. */
  readonly headline: string;
  /** The paragraph under the headline. */
  readonly subcopy?: React.ReactNode;
  /** The sign-in control — normally {@link EmailCodeSignInForm}. */
  readonly children: React.ReactNode;
  /** The tinted note under the form, e.g. how access is granted. */
  readonly notice?: React.ReactNode;
  /** Bottom-left footer text. */
  readonly footerLeft?: string;
  /** Bottom-right footer text. */
  readonly footerRight?: string;
  /** The right-hand hero. */
  readonly hero: SignInHero;
}

/**
 * The branded sign-in screen.
 *
 * @param props - Brand copy, the hero, and the sign-in control to render.
 * @returns The login screen.
 */
export function SignInLayout({
  masthead,
  mastheadLabel,
  eyebrow,
  headline,
  subcopy,
  children,
  notice,
  footerLeft,
  footerRight,
  hero,
}: SignInLayoutProps): React.ReactElement {
  return (
    <main className="grid min-h-screen grid-cols-1 bg-white md:grid-cols-[44%_56%]">
      {/*
        ── Brand column ───────────────────────────────────────────────
        Grid rows rather than flex with `my-auto` on the middle block. Auto
        margins center beautifully until the content is taller than the
        viewport, at which point they push the top of the content off-screen
        where it cannot be scrolled back to — which is exactly what happened
        once the sign-in widget grew. Grid rows overflow downward instead, so
        the page simply scrolls.
      */}
      <div className="grid grid-rows-[auto_1fr_auto] gap-6 px-6 py-7 md:px-[7vw] md:pt-[34px] md:pb-7">
        <div className="flex items-center gap-[18px]">
          {masthead}
          {mastheadLabel === undefined ? null : (
            <span className="border-l border-portal-line pl-[18px] text-[11px] tracking-[2px] text-portal-muted">
              {mastheadLabel}
            </span>
          )}
        </div>

        <div className="w-full max-w-[430px] self-center py-6">
          {eyebrow === undefined ? null : (
            <span className="inline-block rounded bg-brand-tint px-[10px] py-2 text-[10px] font-bold tracking-[1.4px] text-brand-link">
              {eyebrow}
            </span>
          )}

          <h1 className="mt-6 mb-5 text-[clamp(44px,4vw,65px)] leading-none tracking-[-3px] whitespace-pre-line">
            {headline}
          </h1>

          {subcopy === undefined ? null : (
            <p className="text-base leading-[1.65] text-portal-muted">{subcopy}</p>
          )}

          <div className="mt-7">{children}</div>

          {notice === undefined ? null : <div className="mt-[18px]">{notice}</div>}
        </div>

        <div className="flex justify-between text-portal-micro text-portal-muted">
          <span>{footerLeft ?? ''}</span>
          <span>{footerRight ?? ''}</span>
        </div>
      </div>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <aside
        className="relative hidden overflow-hidden text-white md:block"
        style={{ background: `linear-gradient(145deg, ${hero.fromColor}, ${hero.toColor})` }}
      >
        {hero.imageUrl === undefined ? null : (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `linear-gradient(180deg, rgba(0,20,60,.15), rgba(0,28,85,.86)), url(${JSON.stringify(hero.imageUrl)})`,
            }}
            // Decorative: the quote beneath carries the meaning.
            role="presentation"
          />
        )}

        <div className="absolute right-[12%] bottom-[9%] left-[9%]">
          {hero.quote === undefined ? null : (
            /*
              A <blockquote> is the correct element for a standalone pull quote
              but draws no marks, so they are added explicitly.
            */
            <blockquote className="block max-w-[680px] text-[clamp(28px,3vw,46px)] leading-[1.15] font-semibold">
              &ldquo;{hero.quote}&rdquo;
            </blockquote>
          )}
          {hero.statValue === undefined ? null : (
            <p className="mt-[30px] text-[30px] font-bold">{hero.statValue}</p>
          )}
          {hero.statLabel === undefined ? null : (
            <p className="text-portal-tiny text-white/78">{hero.statLabel}</p>
          )}
        </div>
      </aside>
    </main>
  );
}

export interface SignInNoticeProps {
  /** `info` is the neutral grey note; `warning` the amber one; `success` the green one. */
  readonly tone?: 'info' | 'warning' | 'success';
  readonly children: React.ReactNode;
}

/** The tinted note under the sign-in form. */
export function SignInNotice({ tone = 'info', children }: SignInNoticeProps): React.ReactElement {
  const tones = {
    info: 'bg-portal-bg text-portal-muted',
    warning: 'bg-[#fff8ec] text-[#7a5b1e]',
    success: 'border border-emerald-200 bg-emerald-50 text-emerald-900',
  } as const;

  return (
    <p role={tone === 'success' ? 'status' : undefined} className={`rounded-[7px] p-[13px] text-portal-small ${tones[tone]}`}>
      {children}
    </p>
  );
}

// ─── Email-code form ─────────────────────────────────────────────────────────

/** Pulls a readable message out of whatever Clerk threw. */
function clerkErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'errors' in error) {
    const { errors } = error as { errors?: { message?: string; longMessage?: string }[] };
    const first = errors?.[0];
    if (first) return first.longMessage ?? first.message ?? 'Sign-in failed.';
  }
  return error instanceof Error ? error.message : 'Sign-in failed.';
}

/** Whether Clerk refused a sign-in because no account exists for the email. */
function isUnknownAccount(error: unknown): boolean {
  if (typeof error === 'object' && error !== null && 'errors' in error) {
    const { errors } = error as { errors?: { code?: string }[] };
    return errors?.some((entry) => entry.code === 'form_identifier_not_found') ?? false;
  }
  return false;
}

/** Query parameters Clerk adds when an invitation link sends someone back to the portal. */
const CLERK_TICKET_PARAM = '__clerk_ticket';
const CLERK_STATUS_PARAM = '__clerk_status';
/** Clerk's status for an invitee who already has an account. */
const TICKET_STATUS_SIGN_IN = 'sign_in';

/** Reads an invitation ticket from the address, if the page was opened from an accept link. */
function invitationTicket(): { ticket: string; status: string | null } | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const ticket = params.get(CLERK_TICKET_PARAM);
  return ticket ? { ticket, status: params.get(CLERK_STATUS_PARAM) } : null;
}

/** Takes the one-use ticket out of the address bar once it has been read. */
function clearInvitationTicket(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(CLERK_TICKET_PARAM);
  url.searchParams.delete(CLERK_STATUS_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
}

/** The name fields Clerk may still ask for when an invitee sets up their sign-in. */
const NAME_FIELDS = new Set(['first_name', 'last_name']);

export interface EmailCodeSignInFormProps {
  /** Label on the email field. */
  readonly emailLabel?: string;
  /** Placeholder on the email field. */
  readonly emailPlaceholder?: string;
  /**
   * Whether an unknown email may create an account through the same one-time
   * code exchange.
   *
   * True for tenant portals, where an invited member has a membership row
   * waiting but no Clerk account yet and must not dead-end on "couldn't find
   * your account" — the API still refuses anyone without a membership.
   *
   * **False for BCE-internal instances**, where the API treats every account in
   * the instance as staff: there, self-service sign-up would be self-service
   * privilege granting.
   */
  readonly allowSignUp?: boolean;
  /** What to say when the address has no account and `allowSignUp` is false. */
  readonly unknownAccountMessage?: string;
  /**
   * Called when Clerk reports no account for the address, before giving up:
   * a chance to provision one on demand (the admin checks the address against
   * the existing user directory server-side). Return true to retry the
   * sign-in once — a second unknown-account failure then shows
   * {@link unknownAccountMessage}. Runs before the `allowSignUp` fallback.
   */
  readonly onUnknownAccount?: (email: string) => Promise<boolean>;
  /** Overrides `--color-brand` for the button, so the form matches its screen. */
  readonly accentColor?: string;
  /**
   * An address already collected elsewhere — by a screen that had to know it
   * before Clerk could be mounted, because it decides which instance to use.
   */
  readonly initialEmail?: string;
  /**
   * Skip straight to sending the code for {@link initialEmail}, rather than
   * asking the person to press Continue on an address they just typed.
   */
  readonly autoStart?: boolean;
}

/**
 * Passwordless email-code sign-in, on Clerk's headless `useSignIn`.
 *
 * Not Clerk's `<SignIn>` widget: the fields are portal-ui components so the
 * screen looks like BCE rather than a Clerk card dropped onto it. Clerk still
 * owns the code exchange and the session; only the UI is ours.
 *
 * Two steps: email, then the code Clerk emails. Reads the account's supported
 * first factors rather than assuming `email_code`, so a differently-configured
 * instance fails with a clear message instead of a silent dead end.
 *
 * Requires a mounted `ClerkProvider` — callers must not render it otherwise.
 *
 * @param props - Copy, the sign-up policy, and the accent color.
 * @returns The sign-in form.
 */
export function EmailCodeSignInForm({
  emailLabel = 'Work email',
  emailPlaceholder = 'you@company.com',
  allowSignUp = false,
  unknownAccountMessage = 'No account for that address. Access is granted by invitation, so ask your administrator.',
  onUnknownAccount,
  accentColor,
  initialEmail,
  autoStart = false,
}: EmailCodeSignInFormProps): React.ReactElement {
  const { isLoaded, signIn, setActive } = useSignIn();
  const { signUp, isLoaded: signUpLoaded } = useSignUp();

  // `accepting` while an invitation link is being used; `name` when Clerk
  // needs their name to finish creating the account.
  const [step, setStep] = useState<'email' | 'code' | 'accepting' | 'name'>(() => (invitationTicket() ? 'accepting' : 'email'));
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Which flow the code step completes: sign-in for known accounts, sign-up
  // for invited first-timers.
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState(initialEmail ?? '');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Creates the sign-in and asks Clerk to email the code. Throws Clerk errors. */
  async function startSignIn(): Promise<void> {
    // Re-checked here (not only in submitEmail) because narrowing on the
    // destructured `isLoaded` does not carry across function boundaries.
    if (!isLoaded) return;
    const attempt = await signIn.create({ identifier: email.trim() });
    const factor = attempt.supportedFirstFactors?.find(
      (f): f is typeof f & { emailAddressId: string } =>
        f.strategy === 'email_code' && 'emailAddressId' in f,
    );
    if (!factor) {
      setError('This account cannot sign in with an email code. Contact your administrator.');
      return;
    }
    await signIn.prepareFirstFactor({
      strategy: 'email_code',
      emailAddressId: factor.emailAddressId,
    });
    setMode('signin');
    setCode('');
    setStep('code');
  }

  /**
   * Starts the sign-in: create it, find the email-code factor, and ask Clerk
   * to send the code. Unknown accounts try the caller's on-demand provisioning
   * hook first (retrying once), then fall through to sign-up only when the
   * caller opted in.
   */
  async function submitEmail(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!isLoaded || busy) return;
    setError(null);
    setBusy(true);
    try {
      await startSignIn();
    } catch (err) {
      if (isUnknownAccount(err)) {
        if (onUnknownAccount) {
          try {
            if (await onUnknownAccount(email.trim())) {
              try {
                await startSignIn();
                return;
              } catch (retryErr) {
                setError(
                  isUnknownAccount(retryErr)
                    ? unknownAccountMessage
                    : clerkErrorMessage(retryErr),
                );
                return;
              }
            }
          } catch {
            // Provisioning unavailable — fall through to the standard answer.
          }
        }
        if (!allowSignUp) {
          setError(unknownAccountMessage);
          return;
        }
        if (signUpLoaded && signUp) {
          try {
            await signUp.create({ emailAddress: email.trim() });
            await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
            setMode('signup');
            setCode('');
            setStep('code');
            return;
          } catch (signUpErr) {
            setError(clerkErrorMessage(signUpErr));
            return;
          }
        }
      }
      setError(clerkErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // An invitation link: Clerk verified the email when they clicked it and
  // sends them here with a one-use ticket. Finish their sign-in with it, so
  // a first-timer needs no code. Once only (StrictMode double-invokes).
  const ticketUsed = useRef(false);
  useEffect(() => {
    if (ticketUsed.current || !isLoaded || !signUpLoaded) return;
    const invitation = invitationTicket();
    if (!invitation) return;
    ticketUsed.current = true;
    clearInvitationTicket();
    void (async () => {
      try {
        if (invitation.status === TICKET_STATUS_SIGN_IN) {
          const attempt = await signIn.create({ strategy: 'ticket', ticket: invitation.ticket });
          if (attempt.status === 'complete') {
            await setActive({ session: attempt.createdSessionId });
            return;
          }
        } else if (signUp) {
          const attempt = await signUp.create({ strategy: 'ticket', ticket: invitation.ticket });
          if (attempt.status === 'complete') {
            await setActive({ session: attempt.createdSessionId });
            return;
          }
          if (attempt.missingFields.some((field) => NAME_FIELDS.has(field))) {
            setMode('signup');
            setStep('name');
            return;
          }
        }
        setError('We could not finish accepting your invitation. Sign in with your email below.');
        setStep('email');
      } catch (err) {
        setError(`${clerkErrorMessage(err)} Sign in with your email below, or ask for a new invitation.`);
        setStep('email');
      }
    })();
  }, [isLoaded, signUpLoaded]);

  /** Finishes an invitation sign-up once they have given their name. */
  async function submitName(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!isLoaded || !signUp || busy) return;
    setError(null);
    setBusy(true);
    try {
      const attempt = await signUp.update({ firstName: firstName.trim(), lastName: lastName.trim() });
      if (attempt.status === 'complete') {
        await setActive({ session: attempt.createdSessionId });
      } else {
        setError('We could not finish setting up your sign-in. Try again.');
      }
    } catch (err) {
      setError(clerkErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // Sending the code on mount, once, when the address was collected upstream.
  // Guarded by a ref because React StrictMode double-invokes effects in
  // development and a second send would email a second code.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!autoStart || autoStarted.current) return;
    if (!isLoaded || !initialEmail) return;
    autoStarted.current = true;
    void submitEmail({ preventDefault: () => {} } as React.FormEvent);
  }, [autoStart, isLoaded, initialEmail]);

  /** Verifies the code and, on success, activates the session. */
  async function submitCode(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!isLoaded || busy) return;
    setError(null);
    setBusy(true);
    try {
      if (mode === 'signup') {
        if (!signUp) throw new Error('Sign-up is not available.');
        const attempt = await signUp.attemptEmailAddressVerification({ code: code.trim() });
        if (attempt.status === 'complete') {
          await setActive({ session: attempt.createdSessionId });
        } else {
          setError('That code did not complete sign-in. Try again.');
        }
        return;
      }

      const attempt = await signIn.attemptFirstFactor({
        strategy: 'email_code',
        code: code.trim(),
      });
      if (attempt.status === 'complete') {
        // setActive flips Clerk's SignedOut → SignedIn; the app swaps in on its
        // own, so no manual redirect is needed here.
        await setActive({ session: attempt.createdSessionId });
      } else {
        setError('That code did not complete sign-in. Try again.');
      }
    } catch (err) {
      setError(clerkErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  /** Re-sends the code without leaving the code step. */
  async function resend(): Promise<void> {
    if (!isLoaded || busy) return;
    setError(null);
    setBusy(true);
    try {
      if (mode === 'signup') {
        await signUp?.prepareEmailAddressVerification({ strategy: 'email_code' });
        return;
      }
      const factor = signIn.supportedFirstFactors?.find(
        (f): f is typeof f & { emailAddressId: string } =>
          f.strategy === 'email_code' && 'emailAddressId' in f,
      );
      if (factor) {
        await signIn.prepareFirstFactor({
          strategy: 'email_code',
          emailAddressId: factor.emailAddressId,
        });
      }
    } catch (err) {
      setError(clerkErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const accentStyle =
    accentColor === undefined ? undefined : ({ '--color-brand': accentColor } as React.CSSProperties);

  if (step === 'accepting') {
    return (
      <div className="space-y-4" style={accentStyle}>
        <SignInNotice tone="success">Accepting your invitation…</SignInNotice>
      </div>
    );
  }

  if (step === 'name') {
    const ready = firstName.trim() !== '' && lastName.trim() !== '';
    return (
      <form onSubmit={submitName} className="space-y-4" style={accentStyle} noValidate>
        <SignInNotice tone="success">Your invitation is accepted. Add your name to finish setting up your sign-in.</SignInNotice>
        <Field label="First name" htmlFor="invite-first-name" required>
          <TextInput id="invite-first-name" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required autoFocus />
        </Field>
        <Field label="Last name" htmlFor="invite-last-name" required>
          <TextInput id="invite-last-name" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
        </Field>
        {error === null ? null : <ErrorNotice inline message={error} />}
        <Button type="submit" variant="primary" disabled={busy || !ready} className="h-[54px] w-full">
          {busy ? 'Finishing…' : 'Finish and sign in'}
        </Button>
      </form>
    );
  }

  if (step === 'code') {
    return (
      <form onSubmit={submitCode} className="space-y-4" style={accentStyle} noValidate>
        <SignInNotice tone="success">
          We emailed a sign-in code to <b>{email}</b>. Enter it below. It can take a minute to arrive; check your junk folder if you don't see it.
        </SignInNotice>
        <Field label="Sign-in code" htmlFor="code">
          <TextInput
            id="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            autoFocus
            placeholder="123456"
          />
        </Field>

        {error === null ? null : <ErrorNotice inline message={error} />}

        <Button type="submit" variant="primary" disabled={busy} className="h-[54px] w-full">
          {busy ? 'Verifying…' : 'Verify & sign in'}
        </Button>

        <div className="flex items-center gap-4 text-portal-tiny font-bold text-brand">
          <button type="button" onClick={() => void resend()} className="underline-offset-2 hover:underline">
            Resend code
          </button>
          <button
            type="button"
            onClick={() => {
              setStep('email');
              setError(null);
            }}
            className="underline-offset-2 hover:underline"
          >
            Use a different email
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={submitEmail} className="space-y-4" style={accentStyle} noValidate>
      <Field label={emailLabel} htmlFor="email">
        <TextInput
          id="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
          placeholder={emailPlaceholder}
        />
      </Field>

      {error === null ? null : <ErrorNotice inline message={error} />}

      <Button
        type="submit"
        variant="primary"
        disabled={busy || !isLoaded}
        className="h-[54px] w-full"
      >
        {busy ? 'Sending code…' : 'Continue'}
      </Button>
    </form>
  );
}
